import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface StatusBreakdown {
  readonly [status: string]: number;
}
interface HealthSnapshot {
  readonly connectors: { total: number; byStatus: StatusBreakdown };
  readonly flows: { total: number; byStatus: StatusBreakdown; lastRunFailures: number };
  readonly queue: { pending: number; processing: number; completed: number; failed: number; dlq: number; backlog: number };
  readonly webhooks: { total: number; active: number; totalFailures: number };
  readonly sync: { total: number; failed: number; partial: number };
  readonly generatedAt: string;
}

function breakdown(b: StatusBreakdown, noneLabel: string): string {
  const entries = Object.entries(b);
  if (entries.length === 0) return noneLabel;
  return entries.map(([k, v]) => `${k}: ${v}`).join(', ');
}

/** M29 — Integration Health Dashboard (§24 / §33). SLA / "is anything broken"
 * view: per-domain status breakdowns plus failure highlights (flow failures,
 * dead-letter depth, webhook failures, failed/partial syncs) so operators can
 * spot degraded integrations at a glance. */
export function M29IntegrationHealthPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [health, setHealth] = useState<HealthSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiRequest<{ data: HealthSnapshot }>('/v1/eip/monitoring/health', { token })
      .then((r) => { if (alive) { setHealth(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  const degraded = health
    ? health.flows.lastRunFailures > 0 || health.queue.dlq > 0 || health.webhooks.totalFailures > 0 ||
      health.sync.failed > 0 || health.sync.partial > 0
    : false;

  return (
    <main aria-label="integration-health">
      <h1>{t('m29.health.title')}</h1>
      {error && <p role="alert">{error}</p>}
      {loading || !health ? <p>{t('m29.health.loading')}</p> : (
        <>
          <p aria-label="overall-status">{degraded ? t('m29.health.degraded') : t('m29.health.healthy')}</p>
          <section aria-label="health-detail">
            <article aria-label="health-connectors">
              <h2>{t('m29.health.cardConnectors')}</h2>
              <p aria-label="connectors-status">{breakdown(health.connectors.byStatus, t('m29.health.none'))}</p>
            </article>
            <article aria-label="health-flows">
              <h2>{t('m29.health.cardFlows')}</h2>
              <p aria-label="flows-status">{breakdown(health.flows.byStatus, t('m29.health.none'))}</p>
              <p aria-label="flows-failures">{t('m29.health.flowsFailures', { count: health.flows.lastRunFailures })}</p>
            </article>
            <article aria-label="health-queue">
              <h2>{t('m29.health.cardQueue')}</h2>
              <p aria-label="queue-failed">{t('m29.health.queueFailed', { count: health.queue.failed })}</p>
              <p aria-label="queue-dlq">{t('m29.health.queueDlq', { count: health.queue.dlq })}</p>
              <p aria-label="queue-backlog">{t('m29.health.queueBacklog', { count: health.queue.backlog })}</p>
            </article>
            <article aria-label="health-webhooks">
              <h2>{t('m29.health.cardWebhooks')}</h2>
              <p aria-label="webhooks-active">{t('m29.health.webhooksActive', { active: health.webhooks.active, total: health.webhooks.total })}</p>
              <p aria-label="webhooks-failures">{t('m29.health.webhooksFailures', { count: health.webhooks.totalFailures })}</p>
            </article>
            <article aria-label="health-sync">
              <h2>{t('m29.health.cardSync')}</h2>
              <p aria-label="sync-failed">{t('m29.health.syncFailed', { count: health.sync.failed })}</p>
              <p aria-label="sync-partial">{t('m29.health.syncPartial', { count: health.sync.partial })}</p>
            </article>
          </section>
        </>
      )}
    </main>
  );
}
